import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    MatAutocomplete,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger,
    MatOptgroup,
    MatOption,
} from '@angular/material/autocomplete';
import { MatFormField, MatFormFieldModule, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatChipInput, MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import {
    ChipsAutocompleteComponent,
    IconComponent,
    SearchPipe,
    TextRendererPipe,
    TooltipTextComponent,
} from 'vcp-common-ui-lib';
import { KeyValue } from '@angular/common';

@Component({
    selector: 'vc-chips-autocomplete-with-groups',
    imports: [
        MatChipsModule,
        MatLabel,
        TooltipTextComponent,
        MatIcon,
        FormsModule,
        MatAutocompleteTrigger,
        MatChipInput,
        MatAutocomplete,
        MatSuffix,
        MatOption,
        MatFormField,
        MatFormFieldModule,
        MatOptgroup,
        TextRendererPipe,
        SearchPipe,
        IconComponent,
    ],
    templateUrl: './chips-autocomplete-with-groups.component.html',
    styleUrl: './chips-autocomplete-with-groups.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsAutocompleteWithGroupsComponent<T> extends ChipsAutocompleteComponent<T> {
    private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

    filtered!: KeyValue<string, T[]>[];

    /** Dropdown options */
    @Input()
    set optionGroups(val: KeyValue<string, T[]>[]) {
        if (val) {
            this._optionGroups = val;
            this.filtered = this._optionGroups;
        }
    }
    get optionGroups(): KeyValue<string, T[]>[] {
        return this._optionGroups;
    }
    private _optionGroups: KeyValue<string, T[]>[] = [];

    @Input()
    allowAddNew: boolean = true;

    @Input()
    addNewLabel!: string;

    @Input()
    description!: string | null;

    /** Custom function for handling deselection of value */
    @Input()
    removeHandler!: (value: T) => boolean;

    /** Function for rendering group names */
    @Input()
    groupRenderer: (data: string) => string = (value: string): string => `${value}`;

    /** Function for rendering options and selected values */
    @Input()
    optionRenderer: (data: T) => string = (value: T): string => `${value}`;

    /** Function for rendering options inside the hover tooltip */
    @Input()
    tooltipOptionRenderer: (data: T) => string = (value: T): string => `${value}`;

    @Output()
    addNew: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    removeItem: EventEmitter<T> = new EventEmitter<T>();

    @ViewChild('autocompleteFilterInput')
    autocompleteFilterInput!: ElementRef<HTMLInputElement>;

    override selectValue(event: MatAutocompleteSelectedEvent): void {
        if (this.checkIfNoGroupsVisible()) {
            this.addNew.emit(event.option.value);
            return;
        }

        this.values = [...(this.values ?? []), event.option.value];
        this.autocompleteFilterInput.nativeElement.value = '';
        this.filterValue = '';
        this._changeDetectorRef.markForCheck();
    }

    checkIfShowGroup(group: KeyValue<string, T[]>): boolean {
        return group.value.some(this.checkIfShowOption);
    }

    checkIfShowOption = (item: T) => {
        if (this.values?.some((value: T) => this.optionRenderer(value) === this.optionRenderer(item))) {
            return false;
        }

        const regExp = new RegExp('\\b' + this.filterValue, 'gi');
        return regExp.test(this.optionRenderer(item));
    };

    checkIfNoGroupsVisible(): boolean {
        return !this.optionGroups.some((group: KeyValue<string, T[]>) => this.checkIfShowGroup(group));
    }

    checkIfCanDeleteChip(value: T): boolean {
        return this.removeHandler != undefined ? this.removeHandler(value) : true;
    }

    onRemove(value: T): void {
        if (this.checkIfCanDeleteChip(value)) {
            this.removeItem.emit(value);
        }
        this._changeDetectorRef.markForCheck();
    }
}
